import { AccountInfo } from '@azure/msal-browser';

export class User {
  private readonly _accountInfo: AccountInfo;

  private static readonly DISMISS_KEY = 'WELCOME_MESSAGE_DISMISSED';
  private static readonly EMAIL_CLAIM = 'emails';
  private static readonly SIGN_IN_NAME_CLAIM = 'signInNames.emailAddress';
  private static readonly CULTURE_CLAIM = 'extension_Culture';
  private static readonly SERVICE_AGREEMENT_CLAIM = 'extension_HasAgreedToTOS';
  private static readonly SERVICE_ACCOUNT_CLAIM = 'extension_IsServiceAccount';
  private static readonly TENANT_ID_CLAIM = 'extension_TenantID';
  private static readonly SHOW_WELCOME_MESSAGE = 'extension_ShowWelcomeMessage';
  private static readonly IS_INTERNAL_USER_CLAIM = 'extension_IsInternalUser';
  private static readonly PHONE_CLAIM = 'extension_Phone';
  private static readonly GROUPS_CLAIM = 'groups';
  private static readonly EXTERNAL_GROUPS_CLAIM = 'idp_groups';
  private static readonly UPN_CLAIM = 'preferred_username';
  private static readonly EXPIRATION_CLAIM = 'exp';

  constructor(
    accountInfo: AccountInfo,
    private readonly _groups: string[] = null
  ) {
    this._accountInfo = accountInfo;
  }

  public static createDefault(): User {
    return new User(null, []);
  }

  public get culture(): string {
    return this.extractPropertyFromToken<string>(User.CULTURE_CLAIM, null);
  }

  public get tenantId(): string {
    return this.extractPropertyFromToken<string>(User.TENANT_ID_CLAIM, null);
  }

  public get name(): string {
    return this._accountInfo ? this._accountInfo.name : '';
  }

  public get eMail(): string {
    const eMailAddresses = this.extractPropertyFromToken<string[]>(User.EMAIL_CLAIM, null);
    const signInName = this.extractPropertyFromToken<string>(User.SIGN_IN_NAME_CLAIM, null);
    const upn = this.extractPropertyFromToken<string>(User.UPN_CLAIM, null);

    return eMailAddresses ? eMailAddresses[0] : signInName || upn;
  }

  public get showWelcomeMessage(): boolean {
    const wasWelcomeMessageDismissed = sessionStorage.getItem(User.DISMISS_KEY) === 'true';

    if (wasWelcomeMessageDismissed) {
      return false;
    }

    return this.extractPropertyFromToken(User.SHOW_WELCOME_MESSAGE, false);
  }

  public get isServiceAccount(): boolean {
    return this.extractPropertyFromToken(User.SERVICE_ACCOUNT_CLAIM, false);
  }

  public get globalUserIdentifier(): string {
    return this._accountInfo ? this._accountInfo.localAccountId : null;
  }

  public get groups(): string[] {
    return this._groups || this.resolveGroupsFromUserToken();
  }

  public get phone(): string {
    return this.extractPropertyFromToken<string>(User.PHONE_CLAIM, null);
  }

  public get isInternalUser(): boolean {
    return this.extractPropertyFromToken(User.IS_INTERNAL_USER_CLAIM, false);
  }

  public get hasAgreedToTermsAndConditions(): boolean {
    const isTemporaryAgreementAvailable =
      sessionStorage.getItem(`TOC_${this.globalUserIdentifier}`) === 'true';

    if (isTemporaryAgreementAvailable) {
      return true;
    }

    return this.extractPropertyFromToken(User.SERVICE_AGREEMENT_CLAIM, false);
  }

  public setTermsOfServiceAgreementTemporary(): void {
    sessionStorage.setItem(`TOC_${this.globalUserIdentifier}`, 'true');
  }

  public dismissWelcomeMessageForThisSession(): void {
    sessionStorage.setItem(User.DISMISS_KEY, 'true');
  }

  public isLoginExpired(offsetInSec?: number): boolean {
    const expirationStamp = this.extractPropertyFromToken<number>(User.EXPIRATION_CLAIM, null);

    if (expirationStamp === null) {
      return false;
    }

    const utcNowInSeconds = Date.now() / 1000;
    const expired = expirationStamp < utcNowInSeconds + (offsetInSec || 0);

    return expired;
  }

  private resolveGroupsFromUserToken(): string[] {
    const internalGroups = this.extractPropertyFromToken<string[]>(User.GROUPS_CLAIM, []);
    const externalGroups = this.extractPropertyFromToken<string[]>(User.EXTERNAL_GROUPS_CLAIM, []);

    return [...internalGroups, ...externalGroups];
  }

  private extractPropertyFromToken<T>(propertyName: string, defaultValue: T): T {
    return this._accountInfo &&
      Object.prototype.hasOwnProperty.call(this._accountInfo.idTokenClaims, propertyName)
      ? (this._accountInfo.idTokenClaims[propertyName] as T)
      : defaultValue;
  }
}
